import { css, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import { HighlightOptions, htmlHighlight } from './components/code-snippet.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';

export const markdownStyles = [
  githubMarkdownCSS,
  css`
    .md-code-block {
      display: block;

      &::part(body) {
        padding: 0;
      }

      pre {
        margin: 0;
        padding: var(--sl-spacing-medium) 0 !important;
      }

      /* Shrink-wrap <code> to the widest line (but at least the visible
         width). Without this, <code> is inline and the 100% min-width below
         resolves against the <pre> scroll viewport, so highlighted rows stop
         at the viewport edge and the green is jagged once scrolled. */
      code {
        display: block !important;
        min-width: fit-content !important;
        box-sizing: border-box;
      }

      .line {
        padding: 0 12px;
      }

      .ins-line {
        display: inline-block;
        min-width: 100%;
        box-sizing: border-box;
        background-color: rgba(46, 160, 67, 0.15);
        box-shadow: inset 3px 0 0 var(--sl-color-success-500);
      }

      /* Force empty .ins-line spans to claim a line of height without
         injecting text content (so copy/paste stays clean). */
      .ins-line::before {
        content: '';
        display: inline-block;
        vertical-align: top;
        width: 0;
        height: 1lh;
      }
    }
  `
];

// sha256 of "flakiness.io rules markdown!";
const separator = '2029cf1746e8fe574b219f0c294f5831175983ee3ed1ad77a8e57d7c580a67d1';
const whitespaceRegex = /^\s*$/;

const cache = new WeakMap<TemplateStringsArray, TemplateStringsArray>();
export function markdown(strings: TemplateStringsArray, ...values: any[]): ReturnType<typeof html> {
  let entry = cache.get(strings);
  if (!entry) {
    const markup = marked(trimLeadingIndent(strings.join(separator))) as string;
    entry = markup.split(separator) as unknown as TemplateStringsArray;
    (entry as any).raw = entry;
    cache.set(strings, entry);
  }
  return html(entry, ...values);
}

markdown.ts = codeblock.bind(null, 'typescript');
markdown.js = codeblock.bind(null, 'javascript');
markdown.bash = codeblock.bind(null, 'bash');
markdown.powershell = codeblock.bind(null, 'powershell');
markdown.yaml = codeblock.bind(null, 'yaml');
markdown.toml = codeblock.bind(null, 'toml');

function codeblock(language: string, text: string, title?: string, options?: HighlightOptions): ReturnType<typeof html> {
  return html`
    <sl-card class=md-code-block>
      ${title ? html`<div slot=header>${title}</div>` : nothing}
      ${unsafeHTML(htmlHighlight(trimLeadingIndent(text), language, options))}
    </sl-card>
  `
}

function trimLeadingIndent(text: string) {
  const lines = text.split('\n');
  if (lines.length <= 1)
    return text.trim();
  if (whitespaceRegex.test(lines[0]))
    lines.shift();
  if (lines.length && whitespaceRegex.test(lines[lines.length - 1]))
    lines.pop();
  let minIndent = Infinity;
  for (const line of lines) {
    if (whitespaceRegex.test(line))
      continue;
    const match = line.match(/^\s*/);
    if (!match)
      continue;
    minIndent = Math.min(match[0].length, minIndent);
  }
  if (Object.is(minIndent, Infinity))
    return lines.join('\n');
  return lines.map(line => line.substring(minIndent)).join('\n');
}